import { GraduationCap, BookOpen, Users, Target, Clock, Award, Calendar, MapPin, Building2 } from 'lucide-react';
import ProgramDetail from '@/components/ProgramDetail';
import { useLanguage } from '@/components/LanguageContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const Education = () => {
  const { t } = useLanguage();
  
  const educationDetails = [
    t('programs.education.details.history', 'In 2010, Vision Jeunesse Nouvelle (VJN) Rugerero was established as a branch of VJN Gisenyi, continuing our mission to support street youth and vulnerable populations. The education program initially operated from VJN Gisenyi, managing 15 literacy sites across Gisenyi, Nyamyumba, Rugerero, Nyundo, Rubavu, and Cyanzarwe sectors.'),
    t('programs.education.details.vocational', 'Our Vocational Training Center (VTC) offers comprehensive training in essential trades: Welding, Architecture, Hairdressing, Automobile Mechanics, Arts, and Tailoring (Sewing). Each program is designed to provide practical skills and industry-standard certification.'),
    t('programs.education.details.duration', 'Training duration is structured based on certification: Rwanda TVET Board (6 months training + 1 month internship) or NESA Certification (1 year training + 3 months internship, currently available for Tailoring).'),
    t('programs.education.details.target', 'Our programs serve diverse groups: Vocational Training (16-30 years), Literacy & Numeracy (15+ years), Gold Youth (15-24 years), Library (open to all), and PWD Program (deaf children 5-18 years).')
  ];

  const objectives = [
    t('programs.education.objectives.main', 'To provide accessible, high-quality education and skills training to communities'),
    t('programs.education.objectives.literacy', 'To improve literacy and numeracy skills among children and adults'),
    t('programs.education.objectives.vocational', 'To provide vocational and technical training to youth and adults'),
    t('programs.education.objectives.gender', 'To promote gender equality in education'),
    t('programs.education.objectives.critical', 'To foster critical thinking and problem-solving skills'),
    t('programs.education.objectives.awareness', 'To raise awareness of health, social, and environmental issues'),
    t('programs.education.objectives.collaboration', 'To collaborate with local schools and communities'),
    t('programs.education.objectives.technology', 'To support the integration of technology in education')
  ];

  const selectionCriteria = [
    {
      title: t('programs.education.criteria.vocational.title', 'Vocational Training Center'),
      criteria: [
        t('programs.education.criteria.vocational.age', 'Age: 16 years or above'),
        t('programs.education.criteria.vocational.education', 'Completed Primary School (P6) or above'),
        t('programs.education.criteria.vocational.literacy', 'Basic literacy skills'),
        t('programs.education.criteria.vocational.priority', 'Vulnerable youth prioritized'),
        t('programs.education.criteria.vocational.gender', 'Open to both genders')
      ]
    },
    {
      title: t('programs.education.criteria.instructor.title', 'Literacy Instructors'),
      criteria: [
        t('programs.education.criteria.instructor.residency', 'Residency in the literacy zone'),
        t('programs.education.criteria.instructor.age', 'Age: 21-50 years'),
        t('programs.education.criteria.instructor.education', 'A\' Level minimum'),
        t('programs.education.criteria.instructor.writing', 'Proficiency in writing'),
        t('programs.education.criteria.instructor.volunteer', 'Willingness to volunteer'),
        t('programs.education.criteria.instructor.languages', 'Basic English and French skills')
      ]
    },
    {
      title: t('programs.education.criteria.choir.title', 'Intama za Yezu Choir'),
      criteria: [
        t('programs.education.criteria.choir.age', 'Youth age range'),
        t('programs.education.criteria.choir.passion', 'Passion for music'),
        t('programs.education.criteria.choir.commitment', 'Commitment to regular rehearsals'),
        t('programs.education.criteria.choir.skills', 'Basic vocal skills'),
        t('programs.education.criteria.choir.faith', 'Catholic faith'),
        t('programs.education.criteria.choir.parish', 'Parish affiliation')
      ]
    }
  ];

  const impactStats = [
    {
      value: '15+',
      label: t('programs.education.stats.literacySites', 'Literacy Sites'),
      icon: <MapPin className="h-6 w-6" />
    },
    {
      value: '6',
      label: t('programs.education.stats.vocationalPrograms', 'Vocational Programs'),
      icon: <Building2 className="h-6 w-6" />
    },
    {
      value: '1000+',
      label: t('programs.education.stats.annualGraduates', 'Annual Graduates'),
      icon: <GraduationCap className="h-6 w-6" />
    }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      {/* Introduction Section */}
      <section className="mb-16 bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl shadow-xl p-8 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl font-bold mb-6">
            {t('programs.education.title', 'Education & Vocational Training Program')}
          </h1>
          <p className="text-lg mb-6 text-blue-100">
            {t('programs.education.intro.description', 'Our Education Program is dedicated to empowering youth and communities through comprehensive learning opportunities. We combine traditional education with practical vocational training to create pathways for sustainable development and employment.')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center space-x-2 bg-blue-700/50 px-4 py-2 rounded-full">
              <GraduationCap className="h-5 w-5" />
              <span>{t('programs.education.intro.stats.students', '1000+ Students')}</span>
            </div>
            <div className="flex items-center space-x-2 bg-blue-700/50 px-4 py-2 rounded-full">
              <Building2 className="h-5 w-5" />
              <span>{t('programs.education.intro.stats.programs', '6 Programs')}</span>
            </div>
            <div className="flex items-center space-x-2 bg-blue-700/50 px-4 py-2 rounded-full">
              <MapPin className="h-5 w-5" />
              <span>{t('programs.education.intro.stats.locations', '15+ Locations')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-xl p-8">
        <div className="flex flex-col md:flex-row gap-8 items-start">
          <div className="md:w-1/3">
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="text-blue-600 mb-4">
                <GraduationCap size={48} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {t('programs.education.title', 'Education & Vocational Training Program')}
              </h2>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">
                {t('programs.education.subtitle', 'Empowering Through Education')}
              </h3>
              <p className="text-gray-700">
                {t('programs.education.description', 'Our education program provides comprehensive learning opportunities, from vocational training to literacy programs, ensuring inclusive and quality education for all.')}
              </p>
            </div>
          </div>
          
          <div className="md:w-2/3">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">
                {t('programs.education.details.title', 'Program Details')}
              </h4>
              <div className="space-y-4">
                {educationDetails.map((detail, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-600 font-semibold">{index + 1}</span>
                    </div>
                    <p className="text-gray-700">{detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats Section */}
      <section className="mt-16 bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-xl p-8">
        <h2 className="text-3xl font-bold mb-8 flex items-center text-gray-900">
          <Target className="h-8 w-8 mr-3 text-blue-600" />
          {t('programs.education.impact', 'Our Impact')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {impactStats.map((stat, index) => (
            <div key={index} className="bg-white rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-shadow">
              <div className="text-blue-600 mb-4 flex justify-center">
                {stat.icon}
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
              <div className="text-gray-700">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Objectives Section */}
      <section className="mt-16 bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-xl p-8">
        <h2 className="text-3xl font-bold mb-8 flex items-center text-gray-900">
          <Target className="h-8 w-8 mr-3 text-blue-600" />
          {t('programs.education.objectives.title', 'Program Objectives')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {objectives.map((objective, index) => (
            <div key={index} className="flex items-start space-x-3 bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <Award className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
              <p className="text-gray-700">{objective}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Selection Criteria Section */}
      <section className="mt-16 bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-xl p-8">
        <h2 className="text-3xl font-bold mb-8 flex items-center text-gray-900">
          <Users className="h-8 w-8 mr-3 text-blue-600" />
          {t('programs.education.criteria.title', 'Selection Criteria')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {selectionCriteria.map((category, index) => (
            <div key={index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-semibold mb-4 text-blue-600">{category.title}</h3>
              <ul className="space-y-3">
                {category.criteria.map((criterion, idx) => (
                  <li key={idx} className="flex items-start space-x-2">
                    <Clock className="h-5 w-5 text-blue-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{criterion}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Problem Statement Section */}
      <section className="mt-16 bg-gradient-to-br from-blue-50 to-white rounded-2xl shadow-xl p-8">
        <h2 className="text-3xl font-bold mb-8 flex items-center text-gray-900">
          <BookOpen className="h-8 w-8 mr-3 text-blue-600" />
          {t('programs.education.context.title', 'Context & Challenges')}
        </h2>
        <div className="prose max-w-none">
          <p className="text-gray-700 mb-4">
            {t('programs.education.context.paragraph1', 'In Rwanda, the education sector faces critical barriers to providing quality, equitable learning opportunities. According to UNESCO\'s "Institute for Statistics 2022" report, Rwanda has 1,967,627 individuals who are illiterate, with females accounting for 1,164,074 of these totals. Local authorities report that the Rubavu District alone has approximately 47,000 illiterate residents spanning various age groups.')}
          </p>
          <p className="text-gray-700">
            {t('programs.education.context.paragraph2', 'These challenges are compounded by limited infrastructure, lack of trained teachers, and cultural or social barriers, which further hinder educational progress. Additionally, economic constraints and displacement prevent families from prioritizing education, leading to high dropout rates, illiteracy, and a lack of vocational skills among young people.')}
          </p>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="mt-16 bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl shadow-xl p-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-4 text-white">
            {t('programs.education.cta.title', 'Get Involved')}
          </h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
            {t('programs.education.cta.description', 'Join us in our mission to provide quality education and vocational training to youth in Rwanda. Your support can make a difference.')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/volunteer">
              <Button className="bg-white text-blue-600 hover:bg-blue-50">
                {t('programs.education.cta.volunteer', 'Volunteer With Us')}
              </Button>
            </Link>
            <Link to="/donate">
              <Button variant="outline" className="border-white text-white hover:bg-blue-700">
                {t('programs.education.cta.donate', 'Support Our Programs')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Education;
